import { useState, useEffect, useRef, Fragment, lazy, Suspense } from 'react'
import { Outlet, useLocation, Link, useNavigate } from 'react-router-dom'
import { Users, Building2, Heart, Newspaper, Phone, ShieldCheck, HardHat, ClipboardCheck, PawPrint, Quote } from 'lucide-react'
import { WaIcon } from './Icons'
import FloatingChat from './FloatingChat'

// GlowMenu (framer-motion) solo es visible en escritorio (.nav-glow-wrapper
// se oculta con display:none en mobile vía CSS) — se carga perezosa y solo
// se monta en desktop para no descargar/ejecutar framer-motion en mobile,
// donde causaba reflows forzados al montar sin necesidad.
const GlowMenu = lazy(() => import('./GlowMenu').then(m => ({ default: m.GlowMenu })))

const NAV_ITEMS = [
  {
    icon: Users,
    label: 'Personas',
    href: '/personas',
    gradient: 'radial-gradient(circle, rgba(206,46,108,0.18) 0%, rgba(144,12,63,0.08) 50%, transparent 100%)',
    iconColor: '#CE2E6C',
    // P-08: submenú creado para los seguros añadidos por la clienta
    submenu: [
      { label: 'Seguro de Arrendamiento', href: '/personas/arrendamiento', desc: 'Respaldo si tu arrendatario incumple' },
      { label: 'Seguro de Bicis y Patinetas', href: '/personas/bicis-patinetas', desc: 'Hurto, daños y responsabilidad civil' },
      { label: 'Seguro Vida y Hogar Deudor', href: '/personas/vida-hogar-deudor', desc: 'Reduce tu cuota sin refinanciar el crédito' },
    ],
  },
  {
    icon: Building2,
    label: 'Empresas',
    href: '/empresas',
    gradient: 'radial-gradient(circle, rgba(61,17,64,0.22) 0%, rgba(61,17,64,0.10) 50%, transparent 100%)',
    iconColor: '#3D1140',
    submenu: [
      { label: 'Seguro Multirriesgo', href: '/empresas/multirriesgo', desc: 'Una sola póliza para todos los riesgos' },
      { label: 'Seguro de Transporte', href: '/empresas/transporte', desc: 'Protección de mercancías en tránsito' },
      { label: 'Seguro de Arrendamiento', href: '/personas/arrendamiento', desc: 'Respaldo si tu arrendatario incumple' },
      // E-04: "Fraude de Empleados" reemplazado por "Seguro de Protección Digital" (la clienta no maneja esa póliza)
      { label: 'Seguro de Protección Digital', href: '/empresas/proteccion-digital', desc: 'Protección frente a riesgos y ataques digitales' }, // TODO(acer): confirmar subtítulo
      // E-05: nueva póliza fuerte que quieren hacer crecer
      { label: 'Seguro de Vida Grupo', href: '/empresas/vida-grupo', desc: 'Protección de vida para tus colaboradores' }, // TODO(acer): confirmar subtítulo
      // E-13: seguros añadidos por la clienta (correo 2026-07)
      { label: 'Seguro de Copropiedades', href: '/empresas/copropiedades', desc: 'Bienes comunes según la Ley 675' },
      { label: 'Seguro de RCE Parqueaderos', href: '/empresas/rce-parqueaderos', desc: 'Vehículos bajo tu custodia' },
      { label: 'Seguro de RCE Profesional', href: '/empresas/rce-profesional', desc: 'Errores y omisiones de tu actividad' },
      { label: 'Seguro de Accidentes Escolares', href: '/empresas/accidentes-escolares', desc: 'Colegios, jardines y universidades' },
      { label: 'Seguro de RCE Hotelera', href: '/empresas/rce-hotelera', desc: 'Huéspedes protegidos según la Ley 2068' },
      // TODO(acer): C-04 ¿agregar "Cumplimiento" al menú Empresas? · C-05 ¿agregar "Salud empresarial"?
    ],
  },
  {
    icon: Heart,
    label: 'Nosotros',
    href: '/nosotros',
    gradient: 'radial-gradient(circle, rgba(192,20,43,0.18) 0%, rgba(144,12,63,0.08) 50%, transparent 100%)',
    iconColor: '#C0142B',
  },
  {
    icon: Phone,
    label: 'Contacto',
    href: '/contacto',
    gradient: 'radial-gradient(circle, rgba(255,195,0,0.22) 0%, rgba(255,195,0,0.10) 50%, transparent 100%)',
    iconColor: '#C8980A',
  },
  {
    icon: HardHat,
    label: 'ARL',
    href: '/arl',
    gradient: 'radial-gradient(circle, rgba(144,12,63,0.20) 0%, rgba(144,12,63,0.08) 50%, transparent 100%)',
    iconColor: '#900C3F',
    submenu: [
      { label: 'Conciliación de Deudas Presuntas', href: '/conciliacion-deudas-presuntas-con-la-arl', desc: 'Concilia deudas presuntas con tu ARL, sin costo' },
    ],
  },
  {
    icon: ClipboardCheck,
    label: 'SG-SST',
    href: '/sgsst',
    gradient: 'radial-gradient(circle, rgba(200,152,10,0.22) 0%, rgba(200,152,10,0.10) 50%, transparent 100%)',
    iconColor: '#C8980A',
  },
  {
    icon: PawPrint,
    label: 'Mascotas',
    href: '/mascotas',
    gradient: 'radial-gradient(circle, rgba(206,46,108,0.18) 0%, rgba(144,12,63,0.08) 50%, transparent 100%)',
    iconColor: '#CE2E6C',
  },
  // NAV-01: "Descargas" retirado del menú (la ruta /descargas sigue viva por si se enlaza puntualmente).
  {
    icon: Quote,
    label: 'Testimonios',
    href: '/testimonios',
    gradient: 'radial-gradient(circle, rgba(206,46,108,0.18) 0%, rgba(144,12,63,0.08) 50%, transparent 100%)',
    iconColor: '#CE2E6C',
    submenu: [
      { label: 'Noticias', href: '/noticias', desc: 'Novedades y publicaciones de ACER Seguros' },
    ],
  },
]

export { NAV_ITEMS }

export default function Layout() {
  const [navOpen, setNavOpen] = useState(false)
  const [isDesktop, setIsDesktop] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(min-width: 769px)').matches
  )
  const navRef = useRef(null)
  const location = useLocation()
  const navigate = useNavigate()

  useEffect(() => {
    const mq = window.matchMedia('(min-width: 769px)')
    const handler = (e) => setIsDesktop(e.matches)
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [])

  // Scroll reveal — re-runs on every page change
  useEffect(() => {
    window.scrollTo(0, 0)
    const timer = setTimeout(() => {
      const els = document.querySelectorAll('.reveal')
      const io = new IntersectionObserver((entries) => {
        entries.forEach(e => {
          if (e.isIntersecting) { e.target.classList.add('in'); io.unobserve(e.target) }
        })
      }, { threshold: 0.08, rootMargin: '0px 0px -50px 0px' })
      els.forEach(el => { el.classList.remove('in'); io.observe(el) })
      document.querySelectorAll('.card, .dark-card, .city-card').forEach((el, i) => {
        el.style.transitionDelay = `${(i % 4) * 75}ms`
      })
      return () => io.disconnect()
    }, 50)
    return () => clearTimeout(timer)
  }, [location.pathname])

  // Nav shadow on scroll
  useEffect(() => {
    const nav = navRef.current
    const onScroll = () => {
      if (!nav) return
      nav.style.borderBottomColor = window.scrollY > 20
        ? 'rgba(144,12,63,0.2)' : 'rgba(144,12,63,0.1)'
    }
    window.addEventListener('scroll', onScroll, { passive: true })
    return () => window.removeEventListener('scroll', onScroll)
  }, [])

  // Lock body when mobile nav is open
  useEffect(() => { document.body.style.overflow = navOpen ? 'hidden' : '' }, [navOpen])

  // Close mobile nav on route change
  useEffect(() => { setNavOpen(false) }, [location.pathname])

  const closeNav = () => setNavOpen(false)

  return (
    <>
      {/* NAV */}
      <nav ref={navRef} id="nav">
        <Link to="/" className="nav-logo">
          <img src="/logo-acer-color.webp" alt="ACER Seguros" height="66" />
        </Link>

        <div className="nav-right">
          <a href="https://www.instagram.com/AcerSeguros/" className="nav-social" title="Instagram" aria-label="Instagram" target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg>
          </a>
          <Link to="/iniciar-sesion" className="nav-cliente-pill">Iniciar sesión</Link>
          <a href="https://www.facebook.com/AcerSegurosLtda/" className="nav-social" title="Facebook" aria-label="Facebook" target="_blank" rel="noopener noreferrer">
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg>
          </a>
          <Link to="/admin" className="nav-admin-icon" title="Administrador">
            <ShieldCheck size={18} />
          </Link>
          <a href="https://wa.me/573204216449" className="btn btn-ghost" target="_blank" rel="noopener noreferrer">320-421-6449</a>
          <a href="https://wa.me/573204216449?text=Hola%2C%20quiero%20cotizar%20con%20ACER%20Seguros" className="btn btn-primary" target="_blank" rel="noopener noreferrer">
            Cotizar ahora
          </a>
          <button className="nav-toggle" onClick={() => setNavOpen(o => !o)} aria-label="Abrir menú">
            <span /><span /><span />
          </button>
        </div>

        <div className="nav-glow-wrapper">
          {isDesktop && (
            <Suspense fallback={null}>
              <GlowMenu items={NAV_ITEMS} />
            </Suspense>
          )}
        </div>
      </nav>

      {/* MOBILE NAV OVERLAY */}
      <div className={`mobile-nav-overlay${navOpen ? ' open' : ''}`} onClick={closeNav} />

      {/* MOBILE NAV DRAWER */}
      <div className={`mobile-nav${navOpen ? ' open' : ''}`}>
        {NAV_ITEMS.map(item => (
          <Fragment key={item.label}>
            <Link to={item.href} onClick={closeNav}>{item.label}</Link>
            {item.submenu?.map(s => (
              <Link key={s.href} to={s.href} onClick={closeNav} className="mobile-nav-sub">↳ {s.label}</Link>
            ))}
          </Fragment>
        ))}
        <a
          href="https://wa.me/573204216449?text=Hola%2C%20quiero%20cotizar%20con%20ACER%20Seguros"
          target="_blank" rel="noopener noreferrer"
          className="mobile-nav-wa"
        >
          WhatsApp
        </a>
      </div>

      {/* PAGE CONTENT */}
      <Outlet />

      {/* FOOTER */}
      <footer>
        <div className="footer-grid">
          <div>
            <img src="/logo-acer-blanco.webp" alt="ACER Seguros" height="40"
                 style={{marginBottom:'0.75rem',display:'block'}} />
            <div className="footer-desc">Soluciones en seguros para tu familia y empresa. Cobertura a nivel nacional en toda Colombia.</div>
            <div className="footer-social">
              <a href="https://www.facebook.com/AcerSegurosLtda/" aria-label="Facebook" target="_blank" rel="noopener"><svg viewBox="0 0 24 24"><path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/></svg></a>
              <a href="https://www.instagram.com/AcerSeguros/" aria-label="Instagram" target="_blank" rel="noopener"><svg viewBox="0 0 24 24"><rect x="2" y="2" width="20" height="20" rx="5"/><path d="M16 11.37A4 4 0 1112.63 8 4 4 0 0116 11.37z"/><line x1="17.5" y1="6.5" x2="17.51" y2="6.5"/></svg></a>
              <a href="https://wa.me/573113344265?text=Hola%2C%20quiero%20información%20sobre%20ACER%20Seguros" aria-label="WhatsApp" target="_blank" rel="noopener noreferrer"><WaIcon size={14} /></a>
            </div>
          </div>
          <div>
            <div className="footer-col-title">Personas</div>
            <ul className="footer-links">
              {['Automóvil','Hogar','Vida','Salud','Mascotas','Accidentes'].map(s => (
                <li key={s}><Link to="/personas">{s}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            <div className="footer-col-title">Empresas</div>
            <ul className="footer-links">
              {['Cumplimiento','Multirriesgo','Transporte','Ingeniería','Vida Grupo','ARL'].map(s => (
                <li key={s}><Link to="/empresas">{s}</Link></li>
              ))}
            </ul>
          </div>
          <div>
            {/* TODO(acer): E-12 — la clienta reportó "Contáctenos" cortado/truncado, pero ese texto literal
                no existe en el código actual (el footer dice "Contacto"). Confirmar con captura dónde se ve cortado
                (¿nav en móvil?, ¿otro componente?) para corregir el overflow. */}
            <div className="footer-col-title">Contacto</div>
            <ul className="footer-links">
              <li><a href="tel:3113344265">311-334-4265</a></li>
              <li><a href="tel:3104495808">310-449-5808</a></li>
              <li><a href="https://wa.me/573113344265?text=Hola%2C%20quiero%20información%20sobre%20ACER%20Seguros" target="_blank" rel="noopener noreferrer">WhatsApp</a></li>
              <li><span style={{color:'var(--muted)'}}>Cobertura nacional</span></li>
              <li><span style={{color:'var(--muted)'}}>Toda Colombia</span></li>
            </ul>
          </div>
        </div>
        <div className="footer-bottom">
          <span className="footer-copy">© 2026 ACER Seguros. Todos los derechos reservados.</span>
          <span className="footer-copy">Intermediario de seguros — Colombia</span>
        </div>
        <div className="footer-dev">
          <img src="/digital-caratula.webp" alt="Digital Datos" className="footer-dev-logo" />
          <div className="footer-dev-info">
            <span className="footer-dev-label">Desarrollo Web</span>
            <a href="tel:+573171416423" className="footer-dev-tel">+57 317 141 6423</a>
          </div>
        </div>
      </footer>

      <FloatingChat />
    </>
  )
}
